/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

/**
 * Lower bound of each risk-score colour band, ordered from highest to lowest.
 * The corresponding colours are defined in `src/ui/components/risk-score.module.css`.
 */
const bands = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10] as const

/**
 * A band of the risk colour scale, or `unknown` for a device with no score.
 */
export type RiskBand = (typeof bands)[number] | "unknown"

/**
 * Returns the colour band for a risk score.
 *
 * Each band covers a ten-point range starting at its lower bound. Scores below
 * 10 use the lowest band, and scores above 100 use the highest band.
 *
 * @param score - Risk score, absent when the device has none.
 * @returns The matching band, or `unknown` when there is no score.
 *
 * @example
 * riskBand(74) // 70
 * riskBand(undefined) // "unknown"
 */
export function riskBand(score: number | undefined): RiskBand {
    // `== null` rather than `=== undefined`: the generated API types render a
    // nullable field as optional, so a JSON `null` can still arrive here. It
    // would otherwise read as 0 and colour the device green.
    if (score == null || Number.isNaN(score)) {
        return "unknown"
    }
    return bands.find((band) => score >= band) ?? bands[bands.length - 1]
}
